import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import PropTypes from 'prop-types'

import CopyOutlinedIcon from '@ant-design/icons/CopyOutlined'
import DownloadOutlinedIcon from '@ant-design/icons/DownloadOutlined'
import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined'
import LinkOutlinedIcon from '@ant-design/icons/LinkOutlined'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Fade from '@mui/material/Fade'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import DialogTitle from '@components/_mui/DialogTitle'
import Typography from '@components/_mui/Typography'

import Image from '@shared/components/src/Image'
import { cleanUp } from '@shared/providers/src/QueryClientProvider'
import { format, formatError, userRoleToLabel } from '@shared/utils'

import { resolveLogo } from '@utils/Helper'
import CopyToClipboard from '@components/CopyToClipboard'
import config from '@config'
import NotAuthorizedImage from '@assets/errors/401.webp'
import NotFoundImage from '@assets/errors/404.webp'
import ServerErrorImage from '@assets/errors/500.webp'

import { Content, Description, Page } from './ErrorBase.components'

ErrorBase.propTypes = {
  /** The title of the page */
  title: PropTypes.string.isRequired,

  /** The subtitle of the page */
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /** HTTP error response code */
  code: PropTypes.oneOf([401, 404, 500]),

  /** Details of the error  */
  error: PropTypes.object,

  /** Function to reset the error state */
  reset: PropTypes.func,

  /** Whether to hide the back to home button */
  backToHomeForbidden: PropTypes.bool,
}

const codeToImage = {
  401: NotAuthorizedImage,
  404: NotFoundImage,
  500: ServerErrorImage,
}

/**
 * Base component for handling all error pages with standardizing props and styles
 */
export default function ErrorBase({ title, subtitle, code, error, reset = () => undefined, backToHomeForbidden = false }) {
  const [open, setOpen] = useState(false)

  const theme = useTheme()
  const matchDownSM = useMediaQuery(theme.breakpoints.down('sm'))

  const img = codeToImage[code]

  useEffect(() => {
    cleanUp()
  }, [])

  return (
    <Fade in>
      <Page>
        {img ? (
          <Box sx={{ width: { xs: 250, sm: 590 }, height: { xs: 130, sm: 300 } }}>
            <Image src={img} fit="contain" duration={1000} />
          </Box>
        ) : (
          <Box sx={{ width: { xs: 250, sm: 400 }, height: { xs: 70, sm: 110 } }}>
            <Image src={resolveLogo(theme.palette.mode)} fit="contain" duration={1000} />
          </Box>
        )}
        <Content>
          <Description>
            <Typography variant={matchDownSM ? 'h2' : 'h1'} align="center">
              {title}
            </Typography>
            <Typography color="textSecondary" align="center" sx={{ width: '73%' }}>
              {subtitle}
            </Typography>
          </Description>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'center' }}>
            {!backToHomeForbidden && (
              <Button
                component={Link}
                to={config.defaultPath}
                variant="contained"
                onClick={reset}
                size="large"
                sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
              >
                Back To Home
              </Button>
            )}
            {error && (
              <Button
                color="error"
                variant="outlined"
                onClick={() => setOpen(true)}
                startIcon={<ExclamationCircleOutlined />}
                size="large"
                sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
              >
                View Error Details
              </Button>
            )}
          </Box>

          {error && <ErrorDialog error={error} open={open} onClose={() => setOpen(false)} />}
        </Content>
      </Page>
    </Fade>
  )
}

function ErrorDialog({ open, onClose, error }) {
  const { text, html } = formatError(error)
  const details = getErrorDetails(error)
  const honeybadgerUrl = error?.honeybadgerUrl

  const copyText = honeybadgerUrl ? `Honeybadger: ${honeybadgerUrl}\n\n${format(text, html)}` : format(text, html)

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose} scroll="body" slotProps={{ paper: { sx: styles.paper } }}>
      <DialogTitle onClose={onClose}>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          {details.status && (
            <Chip
              label={details.status}
              color="error"
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
            />
          )}
          {details.endpoint && (
            <Typography variant="body2" sx={styles.mono}>
              {details.endpoint}
            </Typography>
          )}
          {details.message && !details.endpoint && (
            <Typography variant="body2" color="error.main">
              {details.message}
            </Typography>
          )}
          {!details.status && !details.endpoint && !details.message && 'Error Details'}
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {/* Honeybadger link */}
        {honeybadgerUrl && (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              px: 3,
              py: 1.5,
              bgcolor: 'action.hover',
            }}
          >
            <LinkOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
            <Typography
              component="a"
              variant="body2"
              href={honeybadgerUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'text.primary', wordBreak: 'break-all', '&:hover': { color: 'primary.main' } }}
            >
              {honeybadgerUrl}
            </Typography>
          </Stack>
        )}

        {/* Structured detail rows */}
        <Box component="dl" sx={styles.detailList}>
          {details.type && <DetailRow label="Type" value={details.type} />}
          {details.statusText && <DetailRow label="Status" value={`${details.status} ${details.statusText}`} />}
          {details.endpoint && <DetailRow label="Endpoint" value={details.endpoint} mono />}
          {details.message && <DetailRow label="Message" value={details.message} />}
          {details.responseMessage && <DetailRow label="Response" value={details.responseMessage} />}
          <DetailRow label="Timestamp" value={new Date().toLocaleString()} />
          {details.user && <DetailRow label="User" value={details.user} />}
        </Box>

        {/* Raw JSON */}
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" component="pre" sx={styles.codeBlock}>
            {text}
          </Typography>
        </Box>

        {/* HTML preview */}
        {html && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Box sx={styles.htmlPreview}>
              <iframe
                title="Error Response HTML"
                srcDoc={html}
                style={{ width: '100%', minHeight: '400px', border: 'none', display: 'block', borderRadius: '4px' }}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <CopyToClipboard>
          {({ copy }) => (
            <Button onClick={() => copy(copyText)} variant="outlined" startIcon={<CopyOutlinedIcon />} disabled={!navigator.clipboard}>
              Copy
            </Button>
          )}
        </CopyToClipboard>
        <Button
          onClick={() => downloadErrorDetailsWithUrl(error, honeybadgerUrl)}
          variant="contained"
          startIcon={<DownloadOutlinedIcon />}
          color="primary"
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function DetailRow({ label, value, mono }) {
  return (
    <Box sx={styles.detailRow}>
      <Typography component="dt" variant="caption" sx={{ minWidth: 90, color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography component="dd" variant="body2" sx={mono ? styles.mono : { wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  )
}

function getErrorDetails(error) {
  if (!error) return {}

  const status = error.response?.status
  const statusText = error.response?.statusText
  const method = error.config?.method?.toUpperCase()
  const url = error.config?.url
  const endpoint = method && url ? `${method} ${url}` : undefined

  const responseData = error.response?.data
  const responseMessage =
    responseData?.errors?.map((e) => (Array.isArray(e.detail) ? e.detail.join(', ') : e.detail)).join('; ') ||
    responseData?.error ||
    undefined

  const { id, role, fullName } = window?.user || {}
  const user = id ? `[${id}] ${[userRoleToLabel[role] || role, fullName].filter(Boolean).join(' - ')}` : undefined

  let type
  if (error.isAxiosError) type = 'Network Error'
  else if (error.name) type = error.name
  else type = 'Error'

  return { type, status, statusText, endpoint, message: error.message, responseMessage, user }
}

function downloadErrorDetailsWithUrl(error, honeybadgerUrl) {
  const { text, html } = formatError(error)
  const prefix = honeybadgerUrl ? `Honeybadger: ${honeybadgerUrl}\n\n` : ''
  const content = prefix + format(text, html)

  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `error-details-${new Date().toISOString()}.txt`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

ErrorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  error: PropTypes.object,
}

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  mono: PropTypes.bool,
}

const mono = '"Fira Code", "JetBrains Mono", Monaco, Menlo, "Ubuntu Mono", Consolas, source-code-pro, monospace'

const styles = {
  paper: {},
  mono: {
    fontFamily: mono,
    wordBreak: 'break-all',
  },
  detailList: {
    m: 0,
    px: 3,
    py: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
  },
  detailRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 2,
    m: 0,
  },
  codeBlock: {
    fontFamily: mono,
    fontSize: '0.75rem',
    backgroundColor: 'background.default',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    p: 2,
    mt: 1,
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  htmlPreview: {
    mt: 1,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    overflow: 'hidden',
    backgroundColor: 'background.default',
  },
}
